export class ApiRequestError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly code?: string,
  ) {
    super(message);
    this.name = "ApiRequestError";
  }
}

type ApiEnvelope<T> =
  | { ok: true; data: T }
  | { ok: false; error: { code: string; message: string } };

export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body && !headers.has("content-type")) {
    headers.set("content-type", "application/json");
  }

  const response = await fetch(path, {
    ...init,
    credentials: "include",
    headers,
  });

  const raw = await response.text();
  let payload: ApiEnvelope<T> | null = null;
  if (raw) {
    try {
      payload = JSON.parse(raw) as ApiEnvelope<T>;
    } catch {
      payload = null;
    }
  }

  if (!response.ok || !payload || payload.ok === false) {
    const error =
      payload && payload.ok === false
        ? payload.error
        : {
            code: "HTTP_ERROR",
            message: `Permintaan gagal (HTTP ${response.status})`,
          };
    throw new ApiRequestError(error.message, response.status, error.code);
  }

  return payload.data;
}

export async function apiUpload<T>(
  path: string,
  body: FormData,
): Promise<T> {
  const response = await fetch(path, {
    method: "POST",
    credentials: "include",
    body,
  });
  const raw = await response.text();
  let payload: ApiEnvelope<T> | null = null;
  if (raw) {
    try {
      payload = JSON.parse(raw) as ApiEnvelope<T>;
    } catch {
      payload = null;
    }
  }

  if (!response.ok || !payload || payload.ok === false) {
    const error =
      payload && payload.ok === false
        ? payload.error
        : {
            code: "HTTP_ERROR",
            message:
              response.status === 413
                ? "Ukuran file terlalu besar"
                : `Upload gagal (HTTP ${response.status})`,
          };
    throw new ApiRequestError(error.message, response.status, error.code);
  }

  return payload.data;
}

export type Me = {
  user: {
    id: number;
    username: string;
    email: string | null;
    phone: string | null;
    name: string;
  };
  tenant: {
    id: number;
    name: string;
    slug: string;
    status: string;
  };
  membership: {
    id: number;
    role: string;
    outletId: number | null;
  };
};

export type WebqrisIntegration = {
  id: string;
  connected: boolean;
  apiBaseUrl: string;
  webhookUrl: string;
  webhookUrlConfigured: string | null;
  webhookUrlMatches: boolean | null;
  status: string;
  lastVerifiedAt: string | null;
  lastError: string | null;
  hasApiToken: boolean;
  hasWebhookSecret: boolean;
  secretVersion: number;
  updatedAt: string;
};

export type WhatsappIntegration = {
  id: string;
  connected: boolean;
  apiBaseUrl: string;
  webhookUrl: string | null;
  hasWebhookSecret: boolean;
  status: string;
  lastVerifiedAt: string | null;
  lastError: string | null;
  hasApiKey: boolean;
  updatedAt: string;
};

export type IntegrationsResponse = {
  appUrl: string;
  webqris: WebqrisIntegration | null;
  whatsapp: WhatsappIntegration | null;
};

export type ConnectionResult = {
  status: "active" | "error";
  message: string;
  httpStatus: number;
  device?: {
    name: string | null;
    sender: string | null;
    quota: number;
    status: string;
  } | null;
};

export type OverviewResponse = {
  counts: {
    outlets: number;
    units: number;
    menuItems: number;
    ordersToday: number;
    paidOrdersToday: number;
    revenueToday: number;
  };
  integrations: {
    webqris: string;
    whatsapp: string;
  };
  billing: {
    balance: number;
    billingType: string;
    feeMode: string | null;
    feeAmount: number;
    feeActive: boolean;
  } | null;
  recentOrders: Array<{
    id: string;
    orderNo: string | null;
    total: number;
    paymentStatus: string;
    fulfillmentStatus: string;
    createdAt: string;
  }>;
};

export type PlatformAdmin = {
  id: number;
  username: string;
  email: string | null;
  phone: string | null;
  name: string;
};

export type SessionResponse =
  | ({ realm: "owner" } & Me)
  | { realm: "platform"; admin: PlatformAdmin };

export type PlatformTenant = {
  id: number;
  name: string;
  slug: string;
  status: string;
  billing_type: string;
  deposit_balance: number;
  fee_total: number;
  topup_total: number;
  created_at: string;
  owner_user_id: number | null;
  owner_name: string | null;
  owner_username: string | null;
  owner_email: string | null;
  owner_phone: string | null;
  owner_status: string | null;
  outlets: number;
  units: number;
  menu_items: number;
  webqris_status: string | null;
  wa_status: string | null;
  fee_mode: string | null;
  fee_amount: number | null;
  fee_active: boolean | null;
  orders_today: number;
  revenue_today: number;
};

export type PlatformAuditLog = {
  id: number;
  tenant_id: number | null;
  tenant_name: string | null;
  actor_type: string;
  actor_id: number | null;
  actor_name: string | null;
  action: string;
  entity_type: string | null;
  entity_id: string | null;
  before_data: unknown;
  after_data: unknown;
  created_at: string;
};

export type PlatformAuditResponse = {
  logs: PlatformAuditLog[];
};

export type PlatformOverviewResponse = {
  counts: {
    tenants: number;
    activeTenants: number;
    outlets: number;
    units: number;
    menuItems: number;
    ordersToday: number;
    revenueToday: number;
  };
  recentTenants: PlatformTenant[];
};

export type PlatformTenantsResponse = {
  tenants: PlatformTenant[];
};
